import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  People as PrincipalsIcon,
  AssignmentTurnedIn as ApprovalIcon,
  FollowTheSigns as FollowUpIcon,
  LockClock as FocusDefenceIcon,
  EventAvailable as BriefingIcon,
  OpenInNew as OpenIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import {
  calendarApi,
  calendarConflictsApi,
  dailyPlanApi,
  decisionsApi,
  workItemsApi,
} from '../api/services';
import type {
  CalendarConflict,
  CalendarEvent,
  DailyPlan,
  Decision,
  WorkItem,
} from '../types/models';

import { usePrincipal, type Principal } from '../context/PrincipalContext';
import { eaLandingCopy } from '../copy/eaLanding';
import {
  deriveAwaitingApproval,
  deriveFocusDefenceAlerts,
  deriveFollowUpsDueToday,
  deriveNextMeetings,
  type AwaitingApprovalRow,
  type FocusDefenceRow,
  type FollowUpRow,
  type NextMeetingRow,
} from '../utils/eaLanding';

// ---------------------------------------------------------------------------
// EaLandingPage — what an Executive Assistant sees at `/`.
//
// Cross-principal signal, four cards:
//   1. Awaiting approval — Proposed plans + pending decisions
//   2. Follow-ups due today — FollowUp items that aren't Done/Cancelled
//   3. Focus-defence alerts — conflicts that touch locked focus blocks
//   4. Next meetings — one preview row per principal
//
// Fan-out over per-principal API calls. Derivations live in `utils/eaLanding`
// so the page reads top-down and the logic is unit-testable. Clicks flip the
// `PrincipalContext` to the relevant principal before navigating, so the
// target page already knows whose data to show.
// ---------------------------------------------------------------------------

interface PrincipalFanoutState {
  principal: Principal;
  plan: DailyPlan | null;
  pendingDecisions: Decision[];
  workItems: WorkItem[];
  conflicts: CalendarConflict[];
  events: CalendarEvent[];
}

export default function EaLandingPage() {
  const navigate = useNavigate();
  const { principals, loading: principalsLoading, setActivePrincipal } = usePrincipal();

  const today = format(new Date(), 'yyyy-MM-dd');
  const prettyDate = format(new Date(), 'EEEE d MMMM');

  const [fanouts, setFanouts] = useState<PrincipalFanoutState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(
    async (list: Principal[]) => {
      if (list.length === 0) {
        setFanouts([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          list.map(async (principal) => {
            const [plan, pendingDecisions, workItems, conflicts, events] = await Promise.all([
              dailyPlanApi.getByDate(today, principal.id).catch((e: any) => {
                if (e?.response?.status === 404) return null;
                throw e;
              }),
              decisionsApi.getPending(principal.id).catch(() => [] as Decision[]),
              workItemsApi.getByDate(today, principal.id).catch(() => [] as WorkItem[]),
              calendarConflictsApi.getByDate(today, principal.id).catch(() => [] as CalendarConflict[]),
              calendarApi.getByDate(today, principal.id).catch(() => [] as CalendarEvent[]),
            ]);
            return { principal, plan, pendingDecisions, workItems, conflicts, events };
          }),
        );
        setFanouts(results);
      } catch (e: any) {
        setError(e?.response?.data?.error ?? 'Unable to load today’s workload.');
      } finally {
        setLoading(false);
      }
    },
    [today],
  );

  useEffect(() => {
    if (principalsLoading) return;
    loadAll(principals);
  }, [principals, principalsLoading, loadAll]);

  // ---- Derivations -------------------------------------------------------
  const awaiting = useMemo<AwaitingApprovalRow[]>(
    () => deriveAwaitingApproval(fanouts),
    [fanouts],
  );
  const followUps = useMemo<FollowUpRow[]>(
    () => deriveFollowUpsDueToday(fanouts),
    [fanouts],
  );
  const focusDefence = useMemo<FocusDefenceRow[]>(
    () => deriveFocusDefenceAlerts(fanouts),
    [fanouts],
  );
  const nextMeetings = useMemo<NextMeetingRow[]>(
    () => deriveNextMeetings(fanouts),
    [fanouts],
  );

  // ---- Click helpers -----------------------------------------------------
  // The derivation helpers pass a lightweight PrincipalRef ({id, name}); the
  // context needs the full Principal. Look up the full record before setting.
  const principalById = useMemo(() => {
    const map = new Map<string, Principal>();
    for (const p of principals) map.set(p.id, p);
    return map;
  }, [principals]);

  const openForPrincipal = useCallback(
    (ref: { id: string; name: string }, path: string) => {
      const full = principalById.get(ref.id);
      if (full) setActivePrincipal(full);
      navigate(path);
    },
    [navigate, principalById, setActivePrincipal],
  );

  // ---- Render ------------------------------------------------------------
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PrincipalsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4">{eaLandingCopy.pageHeading}</Typography>
          </Stack>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {prettyDate}. {eaLandingCopy.pageSubheading}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          endIcon={<ArrowIcon />}
          onClick={() => navigate('/ea-workspace')}
        >
          {eaLandingCopy.viewEaWorkspace}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {(loading || principalsLoading) && fanouts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!principalsLoading && principals.length === 0 && (
        <Paper variant="outlined" sx={{ p: 5, mt: 3, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No principals assigned yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Ask your tenant admin to assign you to the executives you support. Once you are assigned,
            this page will show their plans, follow-ups, focus conflicts, and meetings.
          </Typography>
        </Paper>
      )}

      {fanouts.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SectionCard
              heading={eaLandingCopy.awaitingApprovalHeading}
              icon={<ApprovalIcon sx={{ color: 'primary.main' }} />}
              countBadge={awaiting.length}
            >
              {awaiting.length === 0 ? (
                <EmptyRow text={eaLandingCopy.awaitingApprovalEmpty} />
              ) : (
                <Stack spacing={1}>
                  {awaiting.map((row, i) => (
                    <AwaitingApprovalItem
                      key={`${row.principal.id}-${row.kind}-${row.decisionId ?? i}`}
                      row={row}
                      onOpen={() => openForPrincipal(row.principal, row.kind === 'plan' ? '/plan' : '/work-items')}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SectionCard
              heading={eaLandingCopy.followUpsHeading}
              icon={<FollowUpIcon sx={{ color: 'warning.main' }} />}
              countBadge={followUps.length}
            >
              {followUps.length === 0 ? (
                <EmptyRow text={eaLandingCopy.followUpsEmpty} />
              ) : (
                <Stack spacing={1}>
                  {followUps.map((row) => (
                    <FollowUpItem
                      key={row.workItem.id}
                      row={row}
                      onOpen={() => openForPrincipal(row.principal, '/work-items')}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Box>
        </Stack>
      )}

      {fanouts.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SectionCard
              heading={eaLandingCopy.focusDefenceHeading}
              icon={<FocusDefenceIcon sx={{ color: 'error.main' }} />}
              countBadge={focusDefence.length}
            >
              {focusDefence.length === 0 ? (
                <EmptyRow text={eaLandingCopy.focusDefenceEmpty} />
              ) : (
                <Stack spacing={1}>
                  {focusDefence.map((row, i) => (
                    <FocusDefenceItem
                      key={`${row.principal.id}-${i}`}
                      row={row}
                      onOpen={() => openForPrincipal(row.principal, '/focus')}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SectionCard
              heading={eaLandingCopy.briefingPackHeading}
              icon={<BriefingIcon sx={{ color: 'primary.main' }} />}
              countBadge={nextMeetings.filter((r) => r.event).length}
            >
              {nextMeetings.every((r) => r.event === null) ? (
                <EmptyRow text={eaLandingCopy.briefingPackEmpty} />
              ) : (
                <Stack spacing={1}>
                  {nextMeetings.map((row) => (
                    <NextMeetingItem
                      key={row.principal.id}
                      row={row}
                      onOpen={() => openForPrincipal(row.principal, '/calendar')}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Section card shell
// ---------------------------------------------------------------------------

function SectionCard({
  heading,
  icon,
  countBadge,
  children,
}: {
  heading: string;
  icon: React.ReactNode;
  countBadge?: number;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {heading}
            </Typography>
          </Stack>
          {typeof countBadge === 'number' && countBadge > 0 && (
            <Chip
              size="small"
              label={countBadge}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
      {text}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Row items
// ---------------------------------------------------------------------------

function PrincipalBadge({ name }: { name: string }) {
  return (
    <Chip
      label={name}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
        bgcolor: 'rgba(108, 155, 255, 0.12)',
        color: 'primary.main',
      }}
    />
  );
}

function AwaitingApprovalItem({
  row,
  onOpen,
}: {
  row: AwaitingApprovalRow;
  onOpen: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.main' },
      }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <PrincipalBadge name={row.principal.name} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {row.kind === 'plan'
                ? eaLandingCopy.awaitingApprovalPlan
                : eaLandingCopy.awaitingApprovalDecision}
            </Typography>
          </Stack>
        </Box>
        <OpenIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </Stack>
    </Paper>
  );
}

function FollowUpItem({ row, onOpen }: { row: FollowUpRow; onOpen: () => void }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.main' },
      }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.workItem.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <PrincipalBadge name={row.principal.name} />
            {row.chasing && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {eaLandingCopy.followUpChasing.replace('{name}', row.chasing)}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {eaLandingCopy.followUpSince.replace('{date}', row.since.slice(0, 10))}
            </Typography>
          </Stack>
        </Box>
        <OpenIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </Stack>
    </Paper>
  );
}

function FocusDefenceItem({
  row,
  onOpen,
}: {
  row: FocusDefenceRow;
  onOpen: () => void;
}) {
  const { conflict } = row;
  const severityLabel =
    conflict.severity === 'Hard'
      ? eaLandingCopy.focusDefenceHard
      : eaLandingCopy.focusDefenceSoft;
  const severityColor = conflict.severity === 'Hard' ? 'error' : 'warning';

  const timeLabel = `${format(new Date(conflict.startTime), 'HH:mm')} to ${format(new Date(conflict.endTime), 'HH:mm')}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.main' },
      }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {conflict.description}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <PrincipalBadge name={row.principal.name} />
            <Chip
              size="small"
              label={severityLabel}
              color={severityColor as 'error' | 'warning'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {timeLabel}
            </Typography>
          </Stack>
        </Box>
        <OpenIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </Stack>
    </Paper>
  );
}

function NextMeetingItem({
  row,
  onOpen,
}: {
  row: NextMeetingRow;
  onOpen: () => void;
}) {
  if (!row.event) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, opacity: 0.7 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrincipalBadge name={row.principal.name} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {eaLandingCopy.briefingPackNoUpcoming}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const start = format(new Date(row.event.startTime), 'HH:mm');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.main' },
      }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {start} · {row.event.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <PrincipalBadge name={row.principal.name} />
            {row.event.attendees && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.event.attendees}
              </Typography>
            )}
          </Stack>
        </Box>
        <OpenIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </Stack>
    </Paper>
  );
}
